import { useState } from "react";
import { useProjektPauschalen, useRejectSlot, useUpdateSlot } from "../../hooks/usePlanung";
import { formatEur } from "../../lib/planer";
import { addPanel, addPersonBtn, addPill, avatar, avatarXs, bewerbungAblehnenBtn, bewerbungAnnehmenBtn, cancelPill, checkCircle, input, offeneStellenBox, pauschaleChip, personMain, personRow, rateSm, stepBtn, timeSm } from "./planer-styles";
import { defaultPause, einsatzStatusOf, fmtPause, grossFor, h1, initials, nettoFor, pickedCost, statusOf } from "./planer-utils";
import type { Person, Pick, Picked } from "./planer-utils";
import { Zeitfeld } from "./Zeitfeld";
import "../../styles/termin.css";

// ── Gemeinsame Personen-Liste (Anlegen + Bearbeiten) ───────────────────────
// alwaysShowAll=true (Neuer-Termin-Entwurf): komplette Team-Liste, Klick zum
// An-/Abwählen — wie bisher. alwaysShowAll=false (bestehender Termin): NUR die
// zugewiesenen Personen sind sichtbar (1:1 zur Desktop-App); weitere Personen
// kommen ausschließlich über den "+ Person hinzufügen"-Knopf dazu, der eine
// kompakte Auswahlliste der noch freien Personen aufklappt.
export function PersonList({ people, datum, defVon, defBis, picked, setPicked, alwaysShowAll = true, projektId }: {
  people: Person[]; datum: string; defVon: string; defBis: string; picked: Picked; setPicked: (p: Picked) => void; alwaysShowAll?: boolean;
  /** Projekt des Termins — ohne das gibt es keine Pauschalen zur Auswahl. */
  projektId?: string;
}) {
  const [addOpen, setAddOpen] = useState(false);
  /** Ausgeklappte Personen — zu Beginn alle zu, eine neu hinzugefügte steht offen. */
  const [offen, setOffen] = useState<Set<string>>(() => new Set());
  const umschalten = (id: string) => setOffen((v) => { const n = new Set(v); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const { data: pauschalen = [] } = useProjektPauschalen(projektId || undefined);
  const updateSlot = useUpdateSlot(projektId || "planung");
  const rejectSlot = useRejectSlot(projektId || "planung");
  const toggle = (per: Person) => {
    const next = { ...picked };
    if (next[per.id]) delete next[per.id];
    else if (!alwaysShowAll) setOffen((v) => new Set(v).add(per.id));
    if (!picked[per.id]) next[per.id] = { von: defVon, bis: defBis, stundenlohn: per.role === "helfer" ? per.rate : 0, pause: defaultPause(grossFor(datum, defVon, defBis)), note: "", confirm_status: "geplant", einladungs_status: per.role === "helfer" ? "wartet_auf_helfer" : "zugesagt", zusatzkosten: 0, verguetungsart: "stundenlohn", pauschale_id: null };
    setPicked(next);
    if (!alwaysShowAll) setAddOpen(false);
  };
  const patch = (id: string, p: Partial<Pick>) => setPicked({ ...picked, [id]: { ...picked[id], ...p } });
  const count = Object.keys(picked).length;
  const assigned = people.filter((per) => !!picked[per.id]);
  const unassigned = people.filter((per) => !picked[per.id]);
  const totalCost = pickedCost(picked, people, datum);

  const renderOffRow = (per: Person) => (
    <div key={per.id} style={personRow}>
      <button onClick={() => toggle(per)} style={personMain}>
        <span style={checkCircle}>+</span>
        <span style={{ ...avatar, background: per.color }}>{initials(per.name)}</span>
        <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 0 }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{per.name}</span>
          <span style={{ fontSize: 12, color: "var(--vg-text-3)" }}>{per.role === "inhaber" ? "Inhaber" : `${formatEur(per.rate, { round: true })}/h`}</span>
        </span>
      </button>
      <span style={{ fontSize: 12, color: "var(--vg-text-4)" }}>{defVon}–{defBis}</span>
    </div>
  );

  const renderOnCard = (per: Person) => {
    const p = picked[per.id];
    const dur = nettoFor(datum, p.von, p.bis, p.pause);
    const einsatzBeendet = new Date(`${datum}T${p.bis}:00`).getTime() <= Date.now();
    const istEinladung = per.role === "helfer" && !einsatzBeendet;
    const st = istEinladung ? einsatzStatusOf(p.einladungs_status) : statusOf(p.confirm_status);
    const istBewerbung = istEinladung && ["wartet_auf_inhaber", "eingeladen"].includes(p.einladungs_status);
    const entscheideBewerbung = (status: "zugesagt" | "abgelehnt_vom_inhaber") => {
      if (!p.slot_id) return;
      if (status === "zugesagt") {
        updateSlot.mutate(
          { slotId: p.slot_id, patch: { einladungs_status: status } },
          { onSuccess: () => patch(per.id, { einladungs_status: status }) },
        );
      } else {
        rejectSlot.mutate(
          { slotId: p.slot_id },
          { onSuccess: () => patch(per.id, { einladungs_status: status }) },
        );
      }
    };
    /* Eine Zeile je Person (14.10.2026, Benjamin: „nur eine Zeile für die Person, von
       wann bis wann sie eingeplant ist — auf die Person klicken, dann ausklappen").
       Ausgeklappt stehen Zeiten, Pause, Vergütung, Notiz, Bestätigen und Entfernen.
       Eine offene Bewerbung zeigt ihre beiden Knöpfe auch zugeklappt — sie wartet
       auf eine Entscheidung. */
    const auf = offen.has(per.id);
    const istPauschal = per.role === "helfer" && p.verguetungsart === "pauschale";
    return (
      <div key={per.id} className={"pl-person" + (auf ? " is-offen" : "") + (p.confirm_status === "bestaetigt" ? " is-bestaetigt" : "")}>
        <button type="button" className="pl-person-zeile" aria-expanded={auf} onClick={() => umschalten(per.id)}>
          <span style={{ ...avatar, background: per.color, flex: "none" }}>{initials(per.name)}</span>
          <span className="pl-person-text">
            <span className="pl-person-name">{per.name}</span>
            <span className="pl-person-zeit">
              {p.von}–{p.bis} · {h1(dur)} h{istPauschal ? " · Pauschale" : ""}
            </span>
          </span>
          <span className="pl-person-status" style={{ background: st.bg, color: st.fg }}>{st.label}</span>
          <span className="pl-person-pfeil" aria-hidden="true">›</span>
        </button>
        {istBewerbung && (
          <div style={{ display: "flex", gap: 6, margin: "0 10px 10px" }}>
            <button type="button" style={bewerbungAnnehmenBtn} disabled={updateSlot.isPending || rejectSlot.isPending} onClick={() => entscheideBewerbung("zugesagt")}>✓ Bewerbung annehmen</button>
            <button type="button" style={bewerbungAblehnenBtn} disabled={updateSlot.isPending || rejectSlot.isPending} onClick={() => entscheideBewerbung("abgelehnt_vom_inhaber")}>Ablehnen</button>
          </div>
        )}
        {(updateSlot.isError || rejectSlot.isError) && istBewerbung && (
          <div style={{ color: "var(--vg-red)", fontSize: 12, margin: "0 10px 8px" }}>Entscheidung konnte nicht gespeichert werden.</div>
        )}
        {auf && (
        <div className="pl-person-mehr">
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <Zeitfeld value={p.von} onChange={(v) => patch(per.id, { von: v })} stil={timeSm} groesse="sm" ariaLabel={`Beginn ${per.name}`} />
          <span style={{ color: "var(--vg-text-4)", fontSize: 12 }}>–</span>
          <Zeitfeld value={p.bis} onChange={(v) => patch(per.id, { bis: v })} stil={timeSm} groesse="sm" ariaLabel={`Ende ${per.name}`} />
          <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }} title="Pause">
            <button style={stepBtn} onClick={() => patch(per.id, { pause: Math.max(0, Math.round((p.pause - 0.25) * 4) / 4) })} disabled={p.pause <= 0} aria-label="Pause kürzer">–</button>
            <span style={{ fontSize: 11, minWidth: 28, textAlign: "center", color: "var(--vg-text-3)" }}>{fmtPause(p.pause)}</span>
            <button style={stepBtn} onClick={() => patch(per.id, { pause: Math.min(4, Math.round((p.pause + 0.25) * 4) / 4) })} aria-label="Pause länger">+</button>
          </span>
          {per.role === "helfer" && (() => {
            /* Vergütungsart direkt am Einsatz wählen — das ist die eine Stelle,
               an der Pauschalen gepflegt werden. Der Stundenlohn bleibt beim
               Umschalten stehen, damit er beim Zurückschalten nicht weg ist. */
            const eigene = pauschalen.filter((x) => x.helfer_id === per.id);
            const istPauschal = p.verguetungsart === "pauschale";
            return (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <select
                  value={istPauschal ? "pauschale" : "stundenlohn"}
                  onChange={(e) => patch(per.id, e.target.value === "pauschale"
                    ? { verguetungsart: "pauschale", pauschale_id: eigene[0]?.id ?? null }
                    : { verguetungsart: "stundenlohn", pauschale_id: null })}
                  style={{ ...input, width: 104, fontSize: 11, padding: "4px 6px" }}
                  disabled={!projektId}
                  title={projektId ? "Stundenlohn oder Pauschale" : "Erst ein Projekt wählen"}
                >
                  <option value="stundenlohn">Stundenlohn</option>
                  <option value="pauschale">Pauschale</option>
                </select>
                {istPauschal ? (
                  eigene.length ? (
                    <select
                      value={p.pauschale_id ?? ""}
                      onChange={(e) => patch(per.id, { pauschale_id: e.target.value || null })}
                      style={{ ...input, width: 168, fontSize: 11, padding: "4px 6px" }}
                      title="Welche Pauschale dieses Projekts"
                    >
                      <option value="">Pauschale wählen …</option>
                      {eigene.map((x) => (
                        <option key={x.id} value={x.id}>
                          {(x.bezeichnung || "Pauschale")} · {formatEur(x.betrag, { round: true })}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span style={pauschaleChip} title="Für diese Person gibt es im Projekt noch keine Pauschale — im Projekt anlegen">
                      keine hinterlegt
                    </span>
                  )
                ) : (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                    <input type="number" step="0.5" min="0" value={p.stundenlohn} onChange={(e) => patch(per.id, { stundenlohn: parseFloat(e.target.value) || 0 })} style={rateSm} title="Stundenlohn dieses Einsatzes (€/h)" />
                    <span style={{ fontSize: 11, color: "var(--vg-text-3)" }}>€/h</span>
                  </span>
                )}
              </span>
            );
          })()}
        </div>
        <input value={p.note} onChange={(e) => patch(per.id, { note: e.target.value })} placeholder="Notiz zum Einsatz (z. B. was gemacht wurde)…" style={{ ...input, marginTop: 8, fontSize: 13 }} />
        <div className="pl-person-taten">
          <button disabled={!einsatzBeendet} onClick={() => patch(per.id, { confirm_status: p.confirm_status === "bestaetigt" ? "geplant" : "bestaetigt" })}
            title={!einsatzBeendet ? "Arbeitszeit kann erst nach dem Einsatz bestätigt werden" : undefined}
            className={"pl-person-tat" + (p.confirm_status === "bestaetigt" ? " is-an" : "")}>
            {p.confirm_status === "bestaetigt" ? "✓ Arbeitszeit bestätigt" : einsatzBeendet ? "Arbeitszeit bestätigen" : "Bestätigen nach dem Einsatz"}
          </button>
          <button type="button" className="pl-person-tat is-weg" onClick={() => toggle(per)}>Aus dem Termin nehmen</button>
        </div>
        </div>
        )}
      </div>
    );
  };

  if (alwaysShowAll) {
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: "var(--vg-text-3)" }}>PERSÖNLICH EINLADEN</span>
          <span style={{ fontSize: 12, color: "var(--vg-text-3)" }}>{count} ausgewählt</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {people.map((per) => (picked[per.id] ? renderOnCard(per) : renderOffRow(per)))}
        </div>
      </div>
    );
  }

  // Bestehender Termin: nur zugewiesene Personen + kompakter "+ Person hinzufügen"-Bereich.
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: "var(--vg-text-3)" }}>EINLADUNGEN &amp; BEWERBUNGEN</span>
        <span style={{ fontSize: 12, color: "var(--vg-text-3)" }}>{assigned.length} von {people.length}{totalCost > 0 ? ` · ${formatEur(totalCost, { round: true })} geplant` : ""}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {assigned.length === 0 && (
          <div style={{ fontSize: 12.5, color: "var(--vg-text-3)", padding: "6px 2px" }}>Noch niemand zugewiesen — unten hinzufügen.</div>
        )}
        {assigned.map(renderOnCard)}
      </div>
      {unassigned.length > 0 && (
        addOpen ? (
          <div style={addPanel}>
            {unassigned.map((per) => (
              <button key={per.id} onClick={() => toggle(per)} style={addPill}>
                <span style={{ ...avatarXs, background: per.color }}>{initials(per.name)}</span>
                <span>{per.name}</span>
                <span style={{ color: "var(--vg-text-3)" }}>{per.role === "inhaber" ? "Inhaber" : `${formatEur(per.rate, { round: true })}/h`}</span>
              </button>
            ))}
            <button onClick={() => setAddOpen(false)} style={cancelPill}>Abbrechen</button>
          </div>
        ) : (
          <button onClick={() => setAddOpen(true)} style={addPersonBtn}>+ Person hinzufügen</button>
        )
      )}
    </div>
  );
}

/** Öffentliche Ausschreibung — bewusst unabhängig von persönlichen Einladungen. */
export function OffeneStellenFeld({ value, frei, min = 0, onChange, kompakt = false }: {
  value: number;
  frei?: number;
  min?: number;
  onChange: (value: number) => void;
  /** Im geöffneten Termin (13.10.2026): eine Zeile — die Erklärung steht dort hinter ⓘ. */
  kompakt?: boolean;
}) {
  const nochFrei = frei ?? value;
  return (
    <div style={kompakt ? { ...offeneStellenBox, padding: "4px 6px 4px 12px" } : offeneStellenBox}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700 }}>{kompakt ? "Offene Stellen" : "Offene Stellen ausschreiben"}</div>
        {kompakt ? (
          value > 0 && <div style={{ fontSize: 12, color: "var(--vg-text-3)", marginTop: 1 }}>{nochFrei} noch frei</div>
        ) : (
          <div style={{ fontSize: 12, color: "var(--vg-text-3)", marginTop: 2 }}>
            Nur diese {value === 1 ? "Stelle ist" : "Stellen sind"} für alle Helfer in der Team-App sichtbar.
            {value > 0 ? ` ${nochFrei} davon ${nochFrei === 1 ? "ist" : "sind"} noch frei.` : ""}
          </div>
        )}
      </div>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 5, flex: "0 0 auto" }}>
        <button type="button" style={stepBtn} disabled={value <= min} onClick={() => onChange(Math.max(min, value - 1))}>–</button>
        <span style={{ minWidth: 24, textAlign: "center", fontWeight: 800 }}>{value}</span>
        <button type="button" style={stepBtn} onClick={() => onChange(value + 1)}>+</button>
      </div>
    </div>
  );
}

/**
 * Offene Stellen im geöffneten Termin (13.10.2026): eine Zeile, die Erklärung
 * hinter ⓘ — vorher stand sie als zwei Sätze dauerhaft unter dem Feld.
 */
export function OffeneStellenZeile(props: { value: number; frei: number; min: number; onChange: (value: number) => void }) {
  const [info, setInfo] = useState(false);
  return (
    <div className="tm-stellen">
      <div className="tm-stellen-feld">
        <OffeneStellenFeld kompakt {...props} />
      </div>
      <button type="button" className="tm-info" aria-expanded={info} aria-label="Was heißt offene Stellen?" onClick={() => setInfo((v) => !v)}>ⓘ</button>
      {info && (
        <div className="tm-info-text">
          Nur offene Stellen sind für alle Helfer in der Team-App sichtbar. Eine persönliche Absage wird nie
          automatisch öffentlich — wer Ersatz sucht, erhöht hier die offenen Stellen.
        </div>
      )}
    </div>
  );
}
